import BaseForm from '../base';
import { ObjectPanelComponent, LatLongColumnsComponent, TwoColumnsComponent, HostComponent } from '../components';
const AI_MODELS_DESC = '<em>Order Matters!</em> The first detected class in the list determines the' +
    " event name.<br> E.g.: if event has both person and vehicle detected and <code>['vehicle', " +
    "'person']</code> is configured here, the event name will be <code>cameras/vehicle</code>";
const AI_OVERRIDE_WARN = '- Only add a value here to override defaults set by AI team';
const CLOUD_LPR_MIN_DETECTION_CONF = 0.2;
const CLOUD_LPR_MIN_OCR_CONF = 0.6;
const CLOUD_LPR_MIN_DETECTION_CONF_DESC = lprConfidenceDesc('detecting a license plate', CLOUD_LPR_MIN_DETECTION_CONF);
const CLOUD_LPR_MIN_OCR_CONF_DESC = lprConfidenceDesc('reading the license plate text', CLOUD_LPR_MIN_OCR_CONF);
function lprConfidenceDesc(typeDescription, defaultValue) {
    return `The minimum confidence score of plate analysis for ${typeDescription} ` +
        'in order to be considered a valid result.<br>' +
        'Scores vary between 0 and 1, where a score close to 1 indicates high confidence. ' +
        'A lower minimum threshold will likely produce more false-positives.<br>' +
        `The default value is ${defaultValue}`;
}
export class CamerasForm extends BaseForm {
    constructor(...args) {
        super(...args);
        this.components = [
            {
                label: 'Camera Type',
                data: {
                    values: [
                        {
                            label: 'Axis',
                            value: 'axis'
                        },
                        {
                            label: 'DeepVision',
                            value: 'deepvision'
                        },
                        {
                            label: 'Milesight Solar',
                            value: 'milesight_cloud'
                        },
                        {
                            label: 'ONVIF',
                            value: 'onvif'
                        },
                        {
                            label: 'Reconyx',
                            value: 'reconyx'
                        },
                        {
                            label: 'T20',
                            value: 't20'
                        }
                    ]
                },
                key: 'type',
                type: 'select',
                defaultValue: 'axis',
                validate: {
                    required: true,
                    onlyAvailableItems: true,
                },
                searchFields: ['label', 'value'],
            },
            new LatLongColumnsComponent({ validate: { required: true } }),
            new TwoColumnsComponent({ key: 'localization' }, {
                label: 'Timezone',
                key: 'timezone',
                type: 'select-timezone',
            }, {
                label: 'Camera Region',
                data: {
                    values: [
                        {
                            label: 'Rockies & Mid-Con',
                            value: 'Rockies & Mid-Con'
                        },
                        {
                            label: 'North TX & OK',
                            value: 'North TX & OK'
                        },
                        {
                            label: 'South TX',
                            value: 'South TX'
                        },
                        {
                            label: 'West TX - Permian',
                            value: 'West TX - Permian'
                        },
                        {
                            label: 'East',
                            value: 'East'
                        },
                        {
                            label: 'West',
                            value: 'West'
                        },
                        {
                            label: 'Midwest',
                            value: 'Midwest'
                        }
                    ]
                },
                key: 'cameraRegion',
                type: 'select'
            }),
            {
                label: 'Serial Number',
                key: 'serial',
                type: 'textfield',
                conditional: {
                    json: { in: [{ var: 'data.type' }, ['reconyx', 'milesight_cloud']] }
                },
                validate: {
                    required: true
                }
            },
            {
                key: 'deepVisionFields',
                type: 'fieldset',
                label: 'DeepVision Fields',
                conditional: {
                    json: { in: [{ var: 'data.type' }, ['deepvision', 'axis', 'onvif', 't20']] }
                },
                components: [
                    new ObjectPanelComponent({
                        key: 'deepvision',
                        label: 'DeepVision',
                        components: [
                            {
                                key: 'id',
                                label: 'Camera ID',
                                description: 'The unique identifier for the camera used by DeepVision, leave blank if not recording in DeepVision',
                                type: 'textfield',
                                validate: {
                                    required: false
                                },
                                logic: [
                                    {
                                        name: 'requireForDeepvision',
                                        trigger: {
                                            type: 'json',
                                            json: { '==': [{ var: 'data.type' }, 'deepvision'] }
                                        },
                                        actions: [
                                            {
                                                name: 'setRequiredTrue',
                                                type: 'property',
                                                property: {
                                                    type: 'boolean',
                                                    value: 'validate.required'
                                                },
                                                state: true
                                            }
                                        ]
                                    },
                                    {
                                        name: 'optionalForNonDeepvision',
                                        trigger: {
                                            type: 'json',
                                            json: { '!=': [{ var: 'data.type' }, 'deepvision'] }
                                        },
                                        actions: [
                                            {
                                                name: 'setRequiredFalse',
                                                type: 'property',
                                                property: {
                                                    type: 'boolean',
                                                    value: 'validate.required'
                                                },
                                                state: false
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    }),
                ]
            },
            {
                key: 'physicalCameraFields',
                type: 'fieldset',
                label: 'Physical Camera Fields',
                conditional: {
                    json: { '!': { in: [{ var: 'data.type' }, ['reconyx', 'milesight_cloud', 'deepvision']] } }
                },
                components: [
                    new TwoColumnsComponent({ key: 'localNetwork' }, {
                        label: 'Local IP',
                        key: 'ip',
                        type: 'textfield',
                    }, {
                        label: 'Local Port',
                        key: 'httpAlt',
                        type: 'number',
                    }),
                    new HostComponent({
                        key: 'externalNetwork',
                        componentOverrides: {
                            ip: {
                                label: 'External Address',
                                key: 'externalIp',
                            },
                            port: {
                                label: 'External Port',
                                key: 'externalPort',
                            }
                        }
                    }),
                    new TwoColumnsComponent({ key: 'credentials' }, {
                        label: 'Username',
                        key: 'username',
                        type: 'textfield',
                    }, {
                        label: 'Password',
                        key: 'password',
                        type: 'customPassword',
                    }),
                    {
                        label: 'PTZ',
                        key: 'hasPTZ',
                        type: 'checkbox',
                    },
                    {
                        label: 'RTSP',
                        key: 'rtspStream',
                        type: 'checkbox',
                    },
                    {
                        label: 'RTSP Frame Rate',
                        description: 'Frames per second (Optional)',
                        key: 'framerate',
                        type: 'number',
                        validate: {
                            min: 3,
                            max: 24
                        },
                        decimalLimit: 0,
                        conditional: {
                            show: true,
                            when: 'rtspStream',
                            eq: 'true'
                        },
                    },
                    {
                        label: 'RTSP Custom Path',
                        description: '(Optional)',
                        key: 'rtspPath',
                        type: 'textfield',
                        conditional: {
                            show: true,
                            when: 'rtspStream',
                            eq: 'true'
                        },
                    },
                    {
                        label: 'RTSP Custom Port',
                        description: '(Optional)',
                        key: 'rtspPort',
                        type: 'number',
                        validate: {
                            min: 0,
                            max: 65535
                        },
                        decimalLimit: 0,
                        conditional: {
                            show: true,
                            when: 'rtspStream',
                            eq: 'true'
                        },
                    },
                    {
                        label: 'Continuous Video',
                        key: 'continuousVideo',
                        type: 'checkbox',
                        description: 'Enable DeviceNode to continuously record video to its local harddrive.',
                    },
                ]
            },
            {
                key: 'nvr',
                label: 'NVR',
                description: 'Select the NVR instance that this camera is connected to.',
                type: 'select',
                dataSrc: 'custom',
                valueProperty: 'value',
                data: {
                    // `nvrList` is passed in as property of evalContext on form component
                    custom: 'return nvrList || []'
                },
                conditional: {
                    json: { '!': { in: [{ var: 'data.type' }, ['reconyx', 'milesight_cloud']] } }
                },
                logic: [
                    {
                        name: 'requiredChanger',
                        trigger: {
                            type: 'simple',
                            simple: {
                                show: true,
                                when: 'type',
                                eq: 'deepvision'
                            }
                        },
                        actions: [
                            {
                                name: 'makeRequired',
                                type: 'property',
                                property: {
                                    label: 'Required',
                                    value: 'validate.required',
                                    type: 'boolean'
                                },
                                state: true
                            }
                        ]
                    }
                ],
            },
            {
                label: 'SOC',
                key: 'mmc',
                type: 'checkbox',
            },
            new ObjectPanelComponent({
                key: 'motion',
                label: 'Motion',
                // ONVIF cameras run VCA "trigger" events (e.g. Bosch Intrusion/Loitering)
                // through the stateless path; this debounce is only meaningful there.
                conditional: {
                    json: { '==': [{ var: 'data.type' }, 'onvif'] },
                },
                components: [
                    {
                        label: 'Trigger Debounce (ms)',
                        key: 'statelessDebounce',
                        type: 'number',
                        description: 'Some ONVIF cameras report a single real motion as many rapid trigger events ' +
                            '(e.g. Bosch VCA Intrusion/Loitering) — each one otherwise becomes its own event, video clip, ' +
                            'and upload, flooding the event list and burning bandwidth on metered links like Starlink. ' +
                            'This merges triggers that arrive within the given window (in milliseconds) into one motion event; ' +
                            'the window keeps extending while triggers keep coming, so one continuous motion = one event. ' +
                            'Higher values merge more aggressively — use for noisy thermal / dam cameras (e.g. 15000). ' +
                            'Default 5000. Set 0 to disable, so every trigger becomes its own event.',
                        defaultValue: 5000,
                        validate: {
                            min: 0,
                            max: 300_000,
                        },
                        decimalLimit: 0,
                    },
                ],
            }),
            // Sits directly above the Jobs panel on purpose: this toggle only appears once
            // EPIC Vehicle Human Detection is enabled inside that panel, so keeping the two
            // adjacent means enabling vehuv4 reveals the toggle right where you already are,
            // instead of somewhere further up the form. The key stays top level
            // (settings.suppressUnverifiedClassification) because that is the exact path the
            // API's EventProcessingActionService reads - only the render position changed.
            {
                label: 'Suppress Unverified Classification',
                key: 'suppressUnverifiedClassification',
                type: 'checkbox',
                customConditional: 'show = !!data.jobs.vehuv4.enabled',
                // Preserve the value while hidden: this is a dormant preference, not something
                // to erase just because vehuv4 is momentarily disabled (form.io clears hidden
                // components' values by default).
                clearOnHide: false,
                description: 'Ingest vehicle/human/animal events as motion until EPIC Vehicle Human Detection ' +
                    '(vehuv4) confirms them, instead of trusting the edge/radar classification immediately. ' +
                    'The edge\'s original guess is preserved for audit but no longer drives the alarm - only vehuv4\'s ' +
                    'confirmation does. Only takes effect while vehuv4 is enabled below.',
            },
            new ObjectPanelComponent({
                key: 'jobs',
                label: 'Jobs',
                collapsed: false,
                components: [{
                        key: 'tabs',
                        type: 'tabs',
                        components: [
                            {
                                label: 'EPIC Vehicle Human Detection',
                                key: 'vehuv4Tab',
                                components: [
                                    {
                                        key: 'vehuv4',
                                        type: 'container',
                                        components: [
                                            {
                                                label: 'module',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'module',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'vehuv4'
                                            },
                                            {
                                                label: 'name',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'name',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'EPIC Vehicle Human Detection'
                                            },
                                            {
                                                label: 'type',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'type',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'analytic'
                                            },
                                            {
                                                label: 'Enabled',
                                                key: 'enabled',
                                                type: 'checkbox',
                                            },
                                            {
                                                label: 'Order',
                                                hidden: true,
                                                // customConditional: 'show = !!data.jobs.vehuv4.enabled',
                                                clearOnHide: false,
                                                mask: false,
                                                delimiter: false,
                                                requireDecimal: false,
                                                inputFormat: 'plain',
                                                truncateMultipleSpaces: false,
                                                key: 'order',
                                                type: 'number',
                                                input: true,
                                                description: '<em>Job ordering is not yet supported...</em>',
                                                disabled: true,
                                                defaultValue: 1
                                            },
                                            {
                                                // https://epic-io.atlassian.net/wiki/spaces/WOR/pages/
                                                // 12891815940/Site+Security+Events+Verification+Cloud
                                                key: 'config',
                                                label: 'Config',
                                                customConditional: 'show = !!data.jobs.vehuv4.enabled',
                                                clearOnHide: false,
                                                hideLabel: true,
                                                type: 'container',
                                                components: [
                                                    {
                                                        label: 'AI Models',
                                                        description: AI_MODELS_DESC,
                                                        data: {
                                                            values: [
                                                                {
                                                                    label: 'Human',
                                                                    value: 'person'
                                                                },
                                                                {
                                                                    label: 'Vehicle',
                                                                    value: 'vehicle'
                                                                },
                                                            ]
                                                        },
                                                        key: 'models',
                                                        type: 'select',
                                                        multiple: true,
                                                        clearOnHide: false,
                                                        defaultValue: ['person', 'vehicle'],
                                                        validate: {
                                                            required: true,
                                                            onlyAvailableItems: true,
                                                        },
                                                        searchFields: ['label', 'value'],
                                                    },
                                                    {
                                                        label: 'Movement Threshold',
                                                        key: 'movementThr',
                                                        description: `Movement Detection Threshold ${AI_OVERRIDE_WARN}`,
                                                        mask: false,
                                                        tableView: false,
                                                        delimiter: false,
                                                        requireDecimal: false,
                                                        inputFormat: 'plain',
                                                        truncateMultipleSpaces: false,
                                                        type: 'number',
                                                        validate: {
                                                            min: 0,
                                                            max: 1
                                                        },
                                                        decimalLimit: 3,
                                                        defaultValue: null,
                                                        clearOnHide: false,
                                                        input: true
                                                    },
                                                    {
                                                        label: 'Detection Threshold',
                                                        key: 'detectionThr',
                                                        description: `Detection Threshold ${AI_OVERRIDE_WARN}`,
                                                        mask: false,
                                                        tableView: false,
                                                        delimiter: false,
                                                        requireDecimal: false,
                                                        inputFormat: 'plain',
                                                        truncateMultipleSpaces: false,
                                                        type: 'number',
                                                        validate: {
                                                            min: 0,
                                                            max: 1
                                                        },
                                                        decimalLimit: 3,
                                                        defaultValue: null,
                                                        clearOnHide: false,
                                                        input: true
                                                    }
                                                ]
                                            }
                                        ]
                                    }
                                ]
                            }, {
                                label: 'EPIC GenAI Summary',
                                key: 'genaiSummaryTab',
                                components: [
                                    {
                                        key: 'genaiSummary',
                                        type: 'container',
                                        components: [
                                            {
                                                label: 'module',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'module',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'genaiSummary'
                                            },
                                            {
                                                label: 'name',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'name',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'EPIC GenAI Summary'
                                            },
                                            {
                                                label: 'type',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'type',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'analytic'
                                            },
                                            {
                                                label: 'Enabled',
                                                key: 'enabled',
                                                type: 'checkbox',
                                            },
                                            {
                                                label: 'Order',
                                                hidden: true,
                                                // customConditional: 'show = !!data.jobs.genaiSummary.enabled',
                                                clearOnHide: false,
                                                mask: false,
                                                delimiter: false,
                                                requireDecimal: false,
                                                inputFormat: 'plain',
                                                truncateMultipleSpaces: false,
                                                key: 'order',
                                                type: 'number',
                                                input: true,
                                                description: '<em>Job ordering is not yet supported...</em>',
                                                disabled: true,
                                                defaultValue: 1
                                            },
                                            {
                                                key: 'config',
                                                label: 'Config',
                                                customConditional: 'show = !!data.jobs.genaiSummary.enabled',
                                                clearOnHide: false,
                                                hideLabel: true,
                                                type: 'container',
                                                components: []
                                            }
                                        ]
                                    }
                                ]
                            }, {
                                label: 'Cloud License Plate Recognition',
                                key: 'epicLprTab',
                                components: [
                                    {
                                        key: 'epicLpr',
                                        type: 'container',
                                        components: [
                                            {
                                                label: 'module',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'module',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'epicLpr'
                                            },
                                            {
                                                label: 'name',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'name',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'Cloud License Plate Recognition'
                                            },
                                            {
                                                label: 'type',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'type',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'analytic'
                                            },
                                            {
                                                label: 'Enabled',
                                                key: 'enabled',
                                                type: 'checkbox',
                                            },
                                            {
                                                label: 'Order',
                                                hidden: true,
                                                clearOnHide: false,
                                                mask: false,
                                                delimiter: false,
                                                requireDecimal: false,
                                                inputFormat: 'plain',
                                                truncateMultipleSpaces: false,
                                                key: 'order',
                                                type: 'number',
                                                input: true,
                                                description: '<em>Job ordering is not yet supported...</em>',
                                                disabled: true,
                                                defaultValue: 1
                                            },
                                            {
                                                key: 'config',
                                                label: 'Config',
                                                customConditional: 'show = !!data.jobs.epicLpr.enabled',
                                                clearOnHide: false,
                                                hideLabel: true,
                                                type: 'container',
                                                components: [
                                                    new ObjectPanelComponent({
                                                        key: 'confidence',
                                                        label: 'Confidence',
                                                        collapsed: false,
                                                        components: [{
                                                                label: 'Minimum Plate Detection Confidence',
                                                                key: 'detection',
                                                                description: CLOUD_LPR_MIN_DETECTION_CONF_DESC,
                                                                mask: false,
                                                                tableView: false,
                                                                delimiter: false,
                                                                requireDecimal: false,
                                                                inputFormat: 'plain',
                                                                truncateMultipleSpaces: false,
                                                                type: 'number',
                                                                validate: {
                                                                    min: 0,
                                                                    max: 1
                                                                },
                                                                decimalLimit: 3,
                                                                clearOnHide: false,
                                                                input: true
                                                            },
                                                            {
                                                                label: 'Minimum Plate Text Confidence',
                                                                key: 'text',
                                                                description: CLOUD_LPR_MIN_OCR_CONF_DESC,
                                                                mask: false,
                                                                tableView: false,
                                                                delimiter: false,
                                                                requireDecimal: false,
                                                                inputFormat: 'plain',
                                                                truncateMultipleSpaces: false,
                                                                type: 'number',
                                                                validate: {
                                                                    min: 0,
                                                                    max: 1
                                                                },
                                                                decimalLimit: 3,
                                                                clearOnHide: false,
                                                                input: true
                                                            }]
                                                    }),
                                                ]
                                            }
                                        ]
                                    }
                                ]
                            },
                            {
                                label: 'Fire Detection',
                                key: 'fireDetectionTab',
                                components: [
                                    {
                                        key: 'fireDetection',
                                        type: 'container',
                                        components: [
                                            {
                                                label: 'module',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'module',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'fireDetection'
                                            },
                                            {
                                                label: 'name',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'name',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'Fire Detection'
                                            },
                                            {
                                                label: 'type',
                                                hidden: true,
                                                tableView: true,
                                                clearOnHide: false,
                                                key: 'type',
                                                type: 'textfield',
                                                input: true,
                                                defaultValue: 'analytic'
                                            },
                                            {
                                                label: 'Enabled',
                                                key: 'enabled',
                                                type: 'checkbox',
                                            },
                                            {
                                                label: 'Order',
                                                hidden: true,
                                                clearOnHide: false,
                                                mask: false,
                                                delimiter: false,
                                                requireDecimal: false,
                                                inputFormat: 'plain',
                                                truncateMultipleSpaces: false,
                                                key: 'order',
                                                type: 'number',
                                                input: true,
                                                description: '<em>Job ordering is not yet supported...</em>',
                                                disabled: true,
                                                defaultValue: 1
                                            },
                                            {
                                                key: 'config',
                                                label: 'Config',
                                                customConditional: 'show = !!data.jobs.fireDetection.enabled',
                                                clearOnHide: false,
                                                hideLabel: true,
                                                type: 'container',
                                                components: [
                                                    {
                                                        label: 'Image Frame Rate',
                                                        key: 'imagesPerSecond',
                                                        description: 'The rate of image frames per second to extract from the video to analyze, ' +
                                                            'calculated as <code>number_of_images / number_of_seconds</code>.<br>' +
                                                            'You need to enter the decimal result that comes from applying the formula above.<br>' +
                                                            'For example:<br>' +
                                                            '- To generate 1 image every 4 seconds: <code>1 / 4 = 0.25</code>.' +
                                                            ' Enter <strong>0.25</strong> as the value.' +
                                                            '- To generate 2 images every second: <code>2 / 1 = 2</code>.' +
                                                            ' Enter <strong>2</strong> as the value.' +
                                                            '- The default is 1 image every second: <code>1 / 1 = 1</code>.' +
                                                            ' Enter <strong>1</strong> as the value.',
                                                        type: 'number',
                                                        validate: {
                                                            min: 0,
                                                            max: 30 // at least until we support cameras with higher framerates
                                                        },
                                                        clearOnHide: true,
                                                        defaultValue: 1,
                                                    },
                                                    {
                                                        label: 'Images Per Video',
                                                        key: 'imagesPerVideo',
                                                        description: 'The number of images (frames) to extract from the event video.<br>' +
                                                            'This specifies the maximum number of images that will be generated. ' +
                                                            'Set to <strong>0</strong> (which is the default value) if no limit is desired, ' +
                                                            'causing all images to be analyzed.<br>' +
                                                            'The actual number of images generated could be less depending on the <code>Image Frame ' +
                                                            'Rate</code> option below and total duration of the video.<br>',
                                                        type: 'number',
                                                        validate: {
                                                            min: 0,
                                                            max: 30
                                                        },
                                                        decimalLimit: 0,
                                                        clearOnHide: true,
                                                        defaultValue: 0,
                                                    },
                                                    {
                                                        label: 'Fire Confirmation Threshold',
                                                        key: 'fireConfirmationThreshold',
                                                        description: 'The minimum number of images (frames) with fire detected for the event to be ' +
                                                            'considered a confirmed detection.<br>' +
                                                            'Set it to a higher number to reduce false-positives. ' +
                                                            'Set it to a lower number to make alarm confirmation more sensitive. ' +
                                                            'The default is <strong>3</strong> images.',
                                                        type: 'number',
                                                        validate: {
                                                            min: 1,
                                                            max: 30 // at least until we support cameras with higher framerates
                                                        },
                                                        clearOnHide: true,
                                                        defaultValue: 3,
                                                    },
                                                ]
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                ]
            })
        ];
    }
}
